/* ----------------------------------------------------------------------
 * CampaignDialog.jsx  (FULL component)
 * -------------------------------------------------------------------- */
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Button,
  TextField, MenuItem, FormControlLabel, Switch
} from "@mui/material";
import { useMutation } from "react-query";
import client from "../../../API";
import { isJSONObject, isJSONString } from "../../../Utils";

const CampaignDialog = ({ open, onClose, info = {} }) => {
  /* -------------- local state -------------- */
  const [name,          setName]          = useState(info.name  ?? "");
  const [brief,         setBrief]         = useState(info.brief ?? "");
  const [campaignSum,   setCampaignSum]   = useState(info.campaign_sum ?? "");
  const [proposalDate,  setProposalDate]  = useState(info.proposal_date ?? "");
  const [productType,   setProductType]   = useState(info.campaign_type ?? "");
  const [status,        setStatus]        = useState(info.campaign_status ?? "");
  const [videoAmount,   setVideoAmount]   = useState(info.video_amount ?? "");
  const [poNumber,      setPONumber]      = useState(info.po_number ?? "");
  const [blitzAuto,     setBlitzAuto]     = useState(info.blitz_autocampaign ?? false);
  const [creatorsJson,  setCreatorsJson]  = useState("");
  const [isAgency,      setIsAgency]      = useState(info.agency ?? false);

  /* ----- NEW photo & catalog flags ----- */
  const [campaignPhoto, setCampaignPhoto] = useState(info.campaign_photo ?? "");
  const [catalog,       setCatalog]       = useState(info.catalog ?? false);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    if (info && info.creators) {
      setCreatorsJson(
        isJSONObject(info.creators)
          ? JSON.stringify(info.creators, null, 2)
          : info.creators
      );
    }
  }, [info]);

  /* ---------------- mutation ---------------- */
  const { mutate: editCampaign } = useMutation(client.campaigns.editAdmin, {
    onSuccess: () => onClose(true),
    onError  : (err) => {
      console.error("Error updating campaign:", err);
      alert(err?.response?.data?.error || "Failed to update campaign.");
    }
  });

  /* ---------------- submit ---------------- */
  const handleSubmit = () => {
    if (!isJSONString(creatorsJson)) {
      alert("Creators must be valid JSON.");
      return;
    }
    editCampaign({
      id            : info.id,
      name,
      brief,
      campaign_sum  : campaignSum,
      proposal_date : proposalDate,
      product_type  : productType,
      status,
      creators      : JSON.parse(creatorsJson),
      video_amount  : videoAmount,
      po_number     : poNumber,
      blitz_auto    : blitzAuto,
      isAgency,
      campaign_photo: campaignPhoto,   // ← NEW
      catalog                       ,   // ← NEW
    });
  };

  /* ---------------- UI ---------------- */
  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{info.id ? "Edit Campaign" : "Create Campaign"}</DialogTitle>

      <DialogContent>
        <TextField label="Campaign Name" fullWidth margin="dense"
          value={name} onChange={(e)=>setName(e.target.value)} required />

        <TextField label="Brief" fullWidth margin="dense" multiline rows={3}
          value={brief} onChange={(e)=>setBrief(e.target.value)} required />

        <TextField label="Campaign Sum" type="number" fullWidth margin="dense"
          value={campaignSum} onChange={(e)=>setCampaignSum(e.target.value)} />

        <TextField label="Proposal Date" type="date" fullWidth margin="dense"
          InputLabelProps={{shrink:true}}
          value={proposalDate} onChange={(e)=>setProposalDate(e.target.value)} />

        <TextField label="Product Type" fullWidth margin="dense"
          value={productType} onChange={(e)=>setProductType(e.target.value)} />

        <TextField label="Status" fullWidth margin="dense"
          value={status} onChange={(e)=>setStatus(e.target.value)} />

        {/* ---------- NEW photo field ---------- */}
        <TextField label="Campaign Photo URL" fullWidth margin="dense"
          value={campaignPhoto} onChange={(e)=>setCampaignPhoto(e.target.value)} />

        {/* ---------- creators JSON ---------- */}
        <TextField label="Creators (JSON)" fullWidth margin="dense" multiline rows={4}
          value={creatorsJson} onChange={(e)=>setCreatorsJson(e.target.value)}
          helperText="Edit creators JSON directly" />

        <TextField label="Video Amount" type="number" fullWidth margin="dense"
          value={videoAmount} onChange={(e)=>setVideoAmount(e.target.value)} />

        <TextField label="PO Number" fullWidth margin="dense"
          value={poNumber} onChange={(e)=>setPONumber(e.target.value)} />

        <TextField label="Blitz Auto Campaign" fullWidth margin="dense" select
          value={blitzAuto ? "true":"false"}
          onChange={(e)=>setBlitzAuto(e.target.value==="true")}>
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </TextField>

        <FormControlLabel control={
          <Switch checked={isAgency} onChange={(e)=>setIsAgency(e.target.checked)} />
        } label="Agency" />

        {/* ---------- NEW catalog switch ---------- */}
        <FormControlLabel control={
          <Switch checked={catalog} onChange={(e)=>setCatalog(e.target.checked)} />
        } label="Catalog (featured)" />

      </DialogContent>

      <DialogActions>
        <Button onClick={()=>onClose(false)}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignDialog;
